import { apiFetcher } from "@/services/API.service";
import {
  Tag,
  TagCategory,
  TagCategorySchema,
  TagMetrics,
  TagMetricsSchema,
} from "@/types";
import {
  validateArrayWithSchema,
  validateWithSchema,
} from "@/utils/dataValidators.zod";

// #region fetchTags
// Get all tags
export async function fetchTags({
  signal,
  retry = false,
}: {
  signal?: AbortSignal;
  retry?: boolean;
}): Promise<
  | {
      rStatus: "success";
      tags: TagCategory[];
    }
  | {
      rStatus: "error";
      message: string;
    }
> {
  try {
    if (retry) {
      console.log("Retrying fetchTags");
    }

    const response = await apiFetcher({
      route: "/tags/get",
      method: "POST",
      signal: signal,
    });

    if (response.status === 200) {
      console.log(response.data);

      const validatedData = validateArrayWithSchema<TagCategory>(
        response.data,
        TagCategorySchema,
        "TagCategory"
      );

      if (!validatedData) {
        return {
          rStatus: "error",
          message: "invalid response: data is not an array of valid tags",
        };
      }

      return {
        rStatus: "success",
        tags: validatedData,
      };
    }

    if (response.status === 400) {
      return { rStatus: "error", message: "bad request" };
    }

    if (response.status === 401 || response.status === 403) {
      return { rStatus: "error", message: "unauthorized" };
    }

    if (response.status === 404) {
      return { rStatus: "error", message: "route not found" };
    }

    if (response.status === 500) {
      return { rStatus: "error", message: "internal server error" };
    }

    if (response.status === 0) {
      if (response.error === "request_aborted") {
        return { rStatus: "error", message: "request_aborted" };
      }

      if (!retry) {
        return await fetchTags({ signal, retry: true });
      }

      return { rStatus: "error", message: "network_error" };
    }

    return { rStatus: "error", message: "failed to fetch" };
  } catch (error) {
    console.error(error);
    return { rStatus: "error", message: "failed to fetch" };
  }
}
// #endregion fetchTags

// #region sendUpdateTag
export async function sendUpdateTag({ tag }: { tag: Tag }): Promise<
  | {
      rStatus: "success";
    }
  | {
      rStatus: "error";
      message: string;
    }
> {
  try {
    const response = await apiFetcher({
      route: "/tag/update",
      method: "POST",
      options: {
        body: {
          tag: {
            id: tag.id,
            name: tag.name,
            color: tag.color,
          },
        },
      },
    });

    if (response.status === 200 || response.status === 202) {
      return { rStatus: "success" };
    }

    if (response.status === 204) {
      return { rStatus: "error", message: "tag not found" };
    }

    if (response.status === 400) {
      return { rStatus: "error", message: "bad request" };
    }

    if (response.status === 401 || response.status === 403) {
      return { rStatus: "error", message: "unauthorized" };
    }

    if (response.status === 404) {
      return { rStatus: "error", message: "route not found" };
    }

    if (response.status === 409) {
      return { rStatus: "error", message: "tag name already exists" };
    }

    if (response.status === 500) {
      return { rStatus: "error", message: "internal server error" };
    }

    if (response.status === 0) {
      if (response.error === "request_aborted") {
        return { rStatus: "error", message: "request_aborted" };
      }

      return { rStatus: "error", message: "network_error" };
    }

    return { rStatus: "error", message: "failed to fetch" };
  } catch (error) {
    console.error(error);
    return { rStatus: "error", message: "failed to fetch" };
  }
}
// #endregion sendUpdateTag

// #region fetchTagMetrics
export async function fetchTagMetrics({
  tagID,
  signal,
  retry = false,
}: {
  tagID: number;
  signal?: AbortSignal;
  retry?: boolean;
}): Promise<
  | {
      rStatus: "success";
      metrics: TagMetrics;
    }
  | {
      rStatus: "error";
      message: string;
    }
> {
  try {
    const response = await apiFetcher({
      route: "/tag/metrics/get",
      method: "POST",
      options: {
        body: {
          tag_id: tagID,
        },
      },
      signal,
    });

    if (response.status === 200) {
      const validatedData = validateWithSchema<TagMetrics>(
        response.data,
        TagMetricsSchema,
        "TagMetrics"
      );

      if (!validatedData) {
        return {
          rStatus: "error",
          message: "invalid response",
        };
      }

      return {
        rStatus: "success",
        metrics: validatedData,
      };
    }

    console.log(response);

    if (response.status === 204) {
      return { rStatus: "error", message: "tag not found" };
    }

    if (response.status === 400) {
      return { rStatus: "error", message: "bad request" };
    }

    if (response.status === 401 || response.status === 403) {
      return { rStatus: "error", message: "unauthorized" };
    }

    if (response.status === 404) {
      return { rStatus: "error", message: "route not found" };
    }

    if (response.status === 500) {
      return { rStatus: "error", message: "internal server error" };
    }

    if (response.status === 0) {
      if (response.error === "request_aborted") {
        return { rStatus: "error", message: "request_aborted" };
      }

      if (!retry) {
        return await fetchTagMetrics({ tagID, signal, retry: true });
      }

      return { rStatus: "error", message: "network_error" };
    }

    return { rStatus: "error", message: "failed to fetch" };
  } catch (error) {
    console.error(error);
    return { rStatus: "error", message: "failed to fetch" };
  }
}

// #endregion fetchTagMetrics
